import { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import useFormHook from "@shared/hooks/useFormHook";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notifySuccess } from "@config/tostifyConfig/tostifyConfig";
import { companiesService } from "@shared/services/companiesService";
import { jobDescriptionsService } from "../services/jobDescriptions";
import LoadingButton from '@shared/components/LoadingButton';

export default function JobDescriptionForm({ onClose, onConfirm, editData, type = 'create' }) {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();
 
    const [loading, setLoading] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState("");
    const [selectedCompany, setSelectedCompany] = useState('');
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(true);
    const [error, setError] = useState(null);

    const validationSchema = yup.object().shape({
        ...(type === 'create' && {
            content: yup.mixed().required(t('common.required')),
        }),
    });
    const { register, handleSubmit, setValue, watch, formState: { errors }, clearErrors } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all'
    });

    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setCompaniesLoading(true);
                const response = await companiesService.getAllCompanies();
                setCompanies(response);
                if (response?.length > 0) {
                    setSelectedCompany(response[0].id);
                }
            } catch (error) {
                console.error('Error fetching companies:', error);
                setError('Failed to load companies');
            } finally {
                setCompaniesLoading(false);
            }
        };
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (selectedCompany) {
            setValue('company', selectedCompany);
        }
    }, [selectedCompany, setValue]);

    useEffect(() => {
        if (editData && type === 'edit') {
          
            
            setValue('description', editData.description);
            setValue('status', editData.active ? '1' : '2');
            setValue('company', editData.empBranch?.id || selectedCompany);
            setSelectedFileName(editData.fileName || '');
            
        }
    }, [editData, type, setValue, selectedCompany]);

    const submitForm = async (data) => {
        setLoading(true);
        setError(null);
        
        try {
            if (type === 'create') {
                const file = fileInputRef.current?.files[0];
                if (!file) {
                    setError('Please select a file');
                    setLoading(false);
                    return;
                }
                
                const formData = new FormData();
                formData.append('content', file);
                formData.append('description', data.description);
                formData.append('empBranchId', data.company);
                formData.append('active', data.status === '1');
                
                
                await jobDescriptionsService.createJobDescription(formData);
                notifySuccess(t('common.createdSuccessfully'));
            } else if (type === 'edit') {
                const updateData = {
                    empBranchId: data.company,
                    description: data.description,
                    active: data.status === '1'
                };
                
                await jobDescriptionsService.updateJobDescription(editData.id, updateData);
                notifySuccess(t('common.updatedSuccessfully'));
            }
            
            onConfirm();
            onClose();
        } catch (error) {
            console.error('Error submitting form:', error);
            setError(error.response?.data?.message || 'An error occurred while saving');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFileName(file?.name || "");
        
        // Clear the content error when a file is selected
        if (file) {
            clearErrors('content');
            // Set the file value in the form
            setValue('content', file);
        }
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {type === 'edit' ? t('organizationalStructure.jobDescriptions.form.editTitle') : t('organizationalStructure.jobDescriptions.form.title')}
                        </h4>
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        <form id="jobDescriptionForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="flex-column">
                                <div className="form-section width-90">
                                    {type === 'create' && (
                                        <div className="form-group">
                                            <span className="span-bold">{t('organizationalStructure.jobDescriptions.form.uploadFile')} <span className="required">*</span></span>
                                            <input type="file"
                                                className="form-control"
                                                style={{ display: 'none' }}
                                                id="fileInput"
                                                ref={fileInputRef}
                                                onChange={handleFileChange} />
                                                {errors.content && (
                                                    <div className="invalid-feedback">{errors.content.message}</div>
                                                )}
                                            <button type="button" className="form-btn btn-green" onClick={() => document.getElementById('fileInput').click()}>
                                                {t('organizationalStructure.jobDescriptions.form.chooseFile')}
                                            </button>
                                            {selectedFileName && (
                                                <div className="selected-file">
                                                    <span className="file-name">{selectedFileName}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {type === 'edit' && (
                                        <div className="form-group">
                                            <span className="span-bold">{t('organizationalStructure.jobDescriptions.form.currentFile')}</span>
                                            <div className="selected-file">
                                                <span className="file-name">{selectedFileName}</span>
                                            </div>
                                                <small className="text-muted">{t('organizationalStructure.jobDescriptions.form.fileCannotBeChanged')}</small>
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <span className="span-bold">{t('users.table.search.options.company')}</span>
                                        <select 
                                            className="small-select" 
                                            {...register('company', { required: true })}
                                            disabled={type === 'edit'}
                                        >
                                            <option value="">{t('common.select')}</option>
                                            {companies.map((company) => (
                                                <option key={company.id} value={company.id}>
                                                    {company.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                        <div className="form-group">
                                            <span className="span-bold">{t('organizationalStructure.jobDescriptions.form.description')}</span>
                                        <textarea className="form-control" maxLength={200} {...register('description', { required: true })} />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.jobDescriptions.form.status')}</span>
                                        <select className="small-select" {...register('status', { required: true })}>
                                            <option value="1">{t('organizationalStructure.jobDescriptions.form.active')}</option>
                                            <option value="2">{t('organizationalStructure.jobDescriptions.form.inactive')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <LoadingButton
                            type="submit"
                            form="jobDescriptionForm"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? t('common.saving') : (type === 'edit' ? t('common.save') : t('common.save'))}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}